import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Tabs,
  Tab,
  Paper,
  Avatar,
  useTheme,
  alpha,
  Fade,
  Grow,
  Link,
} from '@mui/material';
import {
  Quiz,
  People,
  Assessment,
  Speed,
  Security,
  CloudUpload,
  CheckCircle,
  Telegram,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import Signature from '../../components/Signature';
import PasswordField from '../../components/Auth/PasswordField';

const LandingPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  
  // Récupérer l'onglet initial depuis l'état de navigation
  const initialTab = location.state?.openAuthTab ?? 0;
  const [authTab, setAuthTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(true);
    // Si un onglet est spécifié dans l'état, scroller vers le formulaire
    if (location.state?.openAuthTab !== undefined) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [location.state]);

  // Formulaire inscription
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Formulaire connexion
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  const signupMismatch =
    signupData.confirmPassword.length > 0 && signupData.password !== signupData.confirmPassword;

  const handleTabChange = (event, newValue) => {
    setAuthTab(newValue);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      await register({
        name: signupData.name,
        email: signupData.email,
        password: signupData.password,
      });
      toast.success('Inscription réussie ! Bienvenue 🎉');
      
      // Vérifier s'il y a une redirection enregistrée
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData);
      toast.success('Connexion réussie ! 🎉');
      
      // Vérifier s'il y a une redirection enregistrée
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erreur lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Quiz sx={{ fontSize: 40 }} />,
      title: 'Quiz Interactifs',
      description: 'Créez des quiz riches avec QCM, Vrai/Faux et questions ouvertes',
    },
    {
      icon: <CloudUpload sx={{ fontSize: 40 }} />,
      title: 'Médias Riches',
      description: 'Ajoutez des images, audio et vidéos à vos questions',
    },
    {
      icon: <People sx={{ fontSize: 40 }} />,
      title: 'Gestion Étudiants',
      description: 'Suivez les progrès de vos étudiants en temps réel',
    },
    {
      icon: <Assessment sx={{ fontSize: 40 }} />,
      title: 'Statistiques',
      description: 'Analysez les performances avec des tableaux de bord détaillés',
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: 'Timer Intégré',
      description: 'Quiz chronométrés avec gestion automatique du temps',
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: 'Sécurisé',
      description: 'Authentification sécurisée et protection des données',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          pt: 8,
          pb: 12,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            borderRadius: '50%',
            bgcolor: alpha('#fff', 0.1),
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -150,
            left: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            bgcolor: alpha('#fff', 0.05),
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            {/* Texte Hero */}
            <Grid item xs={12} md={6}>
              <Fade in={visible} timeout={1000}>
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    }}
                  >
                    Firdaws Quiz
                  </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 3,
                    opacity: 0.95,
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                  }}
                >
                  Plateforme de Quiz Interactive pour le Coaching de Groupe
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                  }}
                >
                  Créez, partagez et gérez des quiz interactifs avec vos étudiants.
                  Suivez leurs progrès, analysez les résultats et optimisez l'apprentissage
                  grâce à une plateforme moderne et intuitive.
                </Typography>

                {/* Stats */}
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="bold">
                        100%
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Gratuit
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="bold">
                        ∞
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Quiz illimités
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="bold">
                        24/7
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Accessible
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              </Fade>
            </Grid>

            {/* Formulaire Inscription/Connexion */}
            <Grid item xs={12} md={6}>
              <Grow in={visible} timeout={1200}>
              <Paper
                elevation={10}
                sx={{
                  p: 4,
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                }}
              >
                <Tabs
                  value={authTab}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{ mb: 3 }}
                >
                  <Tab label="Connexion" />
                  <Tab label="Inscription" />
                </Tabs>

                {/* Formulaire Connexion */}
                {authTab === 0 && (
                  <Box component="form" onSubmit={handleLogin}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      required
                      value={loginData.email}
                      onChange={(e) =>
                        setLoginData({ ...loginData, email: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <PasswordField
                      label="Mot de passe"
                      required
                      autoComplete="current-password"
                      value={loginData.password}
                      onChange={(e) =>
                        setLoginData({ ...loginData, password: e.target.value })
                      }
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ textAlign: 'right', mb: 2 }}>
                      <Link component={RouterLink} to="/forgot-password" variant="body2">
                        Mot de passe oublié ?
                      </Link>
                    </Box>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={loading}
                      sx={{ py: 1.5, fontSize: '1.1rem' }}
                    >
                      {loading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                  </Box>
                )}

                {/* Formulaire Inscription */}
                {authTab === 1 && (
                  <Box component="form" onSubmit={handleSignup}>
                    <TextField
                      fullWidth
                      label="Nom complet"
                      required
                      value={signupData.name}
                      onChange={(e) =>
                        setSignupData({ ...signupData, name: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      required
                      value={signupData.email}
                      onChange={(e) =>
                        setSignupData({ ...signupData, email: e.target.value })
                      }
                      sx={{ mb: 2 }}
                    />
                    <PasswordField
                      label="Mot de passe"
                      required
                      autoComplete="new-password"
                      inputProps={{ minLength: 6 }}
                      value={signupData.password}
                      onChange={(e) =>
                        setSignupData({ ...signupData, password: e.target.value })
                      }
                      sx={{ mb: 2 }}
                      helperText="Minimum 6 caractères"
                    />
                    <PasswordField
                      label="Confirmer le mot de passe"
                      required
                      autoComplete="new-password"
                      value={signupData.confirmPassword}
                      onChange={(e) =>
                        setSignupData({
                          ...signupData,
                          confirmPassword: e.target.value,
                        })
                      }
                      error={signupMismatch}
                      helperText={signupMismatch ? 'Les mots de passe ne correspondent pas' : ' '}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={loading}
                      sx={{ py: 1.5, fontSize: '1.1rem' }}
                    >
                      {loading ? 'Inscription...' : "S'inscrire"}
                    </Button>
                  </Box>
                )}
              </Paper>
              </Grow>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Fonctionnalités Principales
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Tout ce dont vous avez besoin pour créer et gérer vos quiz
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Grow in={visible} timeout={1000 + index * 200}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Telegram Integration Section */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.info.main, 0.05),
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Telegram sx={{ fontSize: 50, color: '#0088cc' }} />
                  <Typography variant="h4" fontWeight="bold">
                    Intégration Telegram
                  </Typography>
                </Box>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                  Partagez vos quiz directement dans vos groupes Telegram
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Vos étudiants peuvent passer les quiz directement depuis Telegram,
                  sans avoir besoin de créer un compte séparé. Un simple clic sur le lien
                  et ils peuvent commencer !
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <CheckCircle color="success" />
                  <Typography>Authentification automatique via Telegram</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <CheckCircle color="success" />
                  <Typography>Partage facile dans les groupes</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <CheckCircle color="success" />
                  <Typography>Résultats sauvegardés automatiquement</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: alpha('#0088cc', 0.05),
                  border: `2px dashed ${alpha('#0088cc', 0.3)}`,
                }}
              >
                <Telegram sx={{ fontSize: 80, color: '#0088cc', mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Pour vos Étudiants
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Les étudiants peuvent accéder aux quiz via Telegram sans inscription
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Parfait pour les groupes de coaching et formations
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              Prêt à commencer ?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Créez votre compte gratuitement et commencez à créer vos quiz dès maintenant
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckCircle />}
                onClick={() => {
                  setAuthTab(1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                Créer un compte gratuit
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => {
                  setAuthTab(0);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                J'ai déjà un compte
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'background.paper' }}>
        <Signature sx={{ py: 2 }} />
      </Box>
    </Box>
  );
};

export default LandingPage;

